/* eslint-disable no-nested-ternary */
import React from 'react';
import { Article } from 'Types/ArticleTypes';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import setImageSize, {
  formatNumberWithCommas,
  valueToObject,
} from 'Utils/UtilityFunctions';
import styled from 'styled-components';
import {
  maxMobile,
  maxTablet,
  maxWidth,
  minExtraLargeScreen,
  primaryFont, primaryHeadingColor, retina, textBlack, wLight, wMedium, wMLight,
} from 'Utils/utilsStyle';
import { Tooltip } from 'antd';
import ArticleListViewSkeleton from 'Components/common/skeleton/ArticleListViewSkeleton';
import { useArticleDetail } from 'Utils/hooks/useArticleDetail';
import { selectIsPagination } from 'Selector/GlobalSearchResultsSelector';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { actions as flurryActions } from 'Slice/HelperSlice';
import { FEED_GILROY_ROUTE, SEARCH_ROUTE } from 'Utils/Constants';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import HeadingWithShowAll from './HeadingWithShowAll';
import SectionHeading from './SectionHeading';

type Props = {
  heading: string,
  articlesCollection?: Article[],
  loading?: boolean,
  articleCount?: Article[],
  count?: number,
  inGlobalSearch?: boolean,
  query?: string,
} & typeof defaultProps;

const defaultProps = {
  loading: false,
  articlesCollection: [] as Article[],
  articleCount: [] as Article[],
  count: 0,
  inGlobalSearch: false,
  query: '',
};

const StyleWrapper = styled.div`
&.extendedSearch {
  margin-bottom: 16px;
  @media all and (min-width: ${minExtraLargeScreen}) {
    margin-bottom: 24px;
  }
}
.article-container {
  display: flex;
  padding: 8px 0px;
  column-gap: 10px;
  width: 100%;
  align-items: center;
  @media all and (min-width: ${minExtraLargeScreen}) {
    padding: 10px 0px;
    column-gap: 14px;
  }
  @media all and (max-width: ${maxWidth}) , ${retina} {
    padding: 6px 0px;
    column-gap: 10px;
  }
  @media all and (max-width: ${maxTablet}) {
    padding: 4px 0px;
    column-gap: 6px;
  }
  @media all and (max-width: ${maxMobile}) {
    padding: 4px 0px;
    column-gap: 6px;
  }
  &:hover {
    background: #FFF;
    border-radius: 6px;
  }
  .article-info {
    width: calc(100% - 48px);
    display: flex;
    flex-direction: column;
    @media all and (min-width: ${minExtraLargeScreen}) {
      width: calc(100% - 62px);
    }
    @media all and (max-width: ${maxWidth}) , ${retina} {
      width: calc(100% - 48px);
      row-gap: 4px;
    }
    @media all and (max-width: ${maxTablet}) {
      width:calc(100% - 38px);
      row-gap: 2px;
    }
    @media all and (max-width: ${maxMobile}) {
      width: calc(100% - 42px);
      row-gap: 0px;
    }
    .title {
      color: ${textBlack};
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMLight};
      line-height: 18px;
      letter-spacing: -0.52px;
      margin-bottom: 6px;
      max-width: calc(100% - 50px);
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
      &.triggers-bullet-tooltip {
        &.person-designation-tip {
          .ant-tooltip {
            .ant-tooltip-content {
              .ant-tooltip-inner {
                white-space: normal;
              }
            }
          }
        }
      }
      @media all and (min-width: ${minExtraLargeScreen}) {
        font-size: 22px;
        line-height: 23px;
        letter-spacing: -0.99px;
        margin-bottom: 8px
      }
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 16px;
        line-height: 18px;
        letter-spacing: -0.52px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 16px;
        letter-spacing: -0.336px;
        margin-bottom: 4px;
      }
      @media all and (max-width: ${maxMobile}) {
        font-size: 12px;
        line-height: 16px;
        letter-spacing: -0.396px;
        margin-bottom: 2px;
      }      
    }
    .details {
      display: flex;
      align-items: flex-end;
      column-gap: 12px;
      cursor: pointer;
      @media all and (min-width: ${minExtraLargeScreen}) {
        column-gap: 16px;
      }
      @media all and (max-width: ${maxWidth}) , ${retina} {
        column-gap: 12px;
      }
      @media all and (max-width: ${maxTablet}) {
        column-gap: 8px;
      }
      @media all and (max-width: ${maxMobile}) {
        column-gap: 12px;
      }
      .company {
        color: #FA541C;
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wMedium};
        line-height: 16px;
        letter-spacing: -0.424px;
        position: relative;
        &::after {
          content: "";
          min-height: 11px;
          min-width: 1px;
          background-color: rgb(207, 207, 207);
          position: absolute;
          right: -7px;
          bottom: 2px;
        }
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 20px;
          line-height: 21px;
          letter-spacing: -0.424px;
          &::after {
            content: "";
            min-height: 15px;
            right: -8px;
            bottom: 4px;
          }
        }
        @media all and (max-width: ${maxWidth}) , ${retina} {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.424px;
          &::after {
            min-height: 8px;
            right: -7px;
            bottom: 2px;
          }
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: -0.274px;
          &::after {
            min-height: 11px;
            right: -5px;
            bottom: 1px;
          }
        }
        @media all and (max-width: ${maxMobile}) {
          font-size: 12px;
          line-height: 12px;
          letter-spacing: -0.24px;
          &::after {
            min-height: 10px;
            right: -7px;
            bottom: 2px;
          }
        }
      }
      .domain {
        color: ${primaryHeadingColor};
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wLight};
        line-height: 16px;
        letter-spacing: -0.424px;
        margin-bottom: 0px;
        position: relative;
        &::after {
          content: "";
          min-height: 11px;
          min-width: 1px;
          background-color: rgb(207, 207, 207);
          position: absolute;
          right: -7px;
          bottom: 2px;
        }
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 20px;
          letter-spacing: -0.599px;
          line-height: 21px;
          &::after {
            content: "";
            min-height: 15px;
            right: -8px;
            bottom: 4px;
          }
        }
        @media all and (max-width: ${maxWidth}) , ${retina} {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.424px;
          &::after {
            min-height: 11px;
            right: -7px;
            bottom: 2px;
          }
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: -0.274px;
          &::after {
            min-height: 11px;
            right: -5px;
            bottom: 1px;
          }
        }
        @media all and (max-width: ${maxMobile}) {
          font-size: 12px;
          line-height: 12px;
          letter-spacing: -0.24px;
          &::after {
            min-height: 10px;
            right: -7px;
            bottom: 1px;
          }
        }
      }
      .date {
        color: ${primaryHeadingColor};
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wLight};
        line-height: 16px;
        letter-spacing: -0.424px;
        margin-bottom: 0px;
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 20px;
          letter-spacing: -0.599px;
          line-height: 21px;
        }
        @media all and (max-width: ${maxWidth}) , ${retina} {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.424px;
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: -0.274px;
        }
        @media all and (max-width: ${maxMobile}) {
          font-size: 12px;
          line-height: 12px;
          letter-spacing: -0.24px;
        }
      }
    }
  }
}
`;
const ArticleImage = styled.div<{ color: string }>`
  width: 48px;
  cursor: pointer;
  height: 42px;
  border-radius: 6px;
  background-color: #FFF;
  @media all and (min-width: ${minExtraLargeScreen}) {
    width: 62px;
    height: 54px;
    border-radius: 6px;
  }
  @media all and (max-width: ${maxWidth}) , ${retina} {
    width: 48px;
    height: 42px;
    border-radius: 4px;
  }
  @media all and (max-width: ${maxTablet}) {
    width: 38px;
    height: 34px;
    border-radius: 3px;
  }
  @media all and (max-width: ${maxMobile}) {
    width: 34px;
    height: 30px;
    border-radius: 3px;
  }
  .lazy-load-image-background {
    width: 100%;
    height: 100%;
  }
  img {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    border: 2px solid;
    border-color: ${(props) => props.color};
    @media all and (min-width: ${minExtraLargeScreen}) {
      border-radius: 6px;
      border: 3px solid;
      border-color: ${(props) => props.color};
    }
    @media all and (max-width: ${maxWidth}) , ${retina} {
      border-radius: 4px;
      border: 2px solid;
      border-color: ${(props) => props.color};
    }
    @media all and (max-width: ${maxTablet}) {
      border-radius: 3px;
      border: 2px solid;
      border-color: ${(props) => props.color};
    }
  }
`;
const Alert = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
  display: inline-flex;
  height: 18px;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 11px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-left: -6px;
  @media all and (min-width: ${minExtraLargeScreen}) {
    height: 22px;
    padding: 6px 11px;
    border-radius: 5px;
    margin-left: -8px;
  }
  @media all and (max-width: ${maxWidth}) , ${retina} {
    height: 18px;
    padding: 4px 8px;
    gap: 11px;
    border-radius: 4px;
    margin-left: -6px;
  }
  @media all and (max-width: ${maxTablet}) {
    height: 14px;
    padding: 2.5px 5px;
    gap: 7px;
    border-radius: 2px;
    margin-left: -4px;
  }
  @media all and (max-width: ${maxMobile}) {
    height: 13px;
    padding: 4px 7px;
    gap: 8px;
    border-radius: 2px;
    margin-left: -6px;
  }
  p {
    color: #FFF;
    font-family: ${primaryFont};
    font-size: 14px;
    font-weight: ${wMedium};
    line-height: 14px;
    letter-spacing: -0.361px;
    margin-bottom: 0px;
    @media all and (min-width: ${minExtraLargeScreen}) {
      font-size: 16px;
      line-height: 20px;
      letter-spacing: -0.511px;
    }
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 14px;
      line-height: 14px;
      letter-spacing: -0.361px;
    }
    @media all and (max-width: ${maxTablet}) {
      font-size: 10px;
      line-height: 12px;
      letter-spacing: -0.233px;
    }
    @media all and (max-width: ${maxMobile}) {
      font-size: 10px;
      line-height: 12px;
      letter-spacing: -0.268px;
    }
  }
`;
const ArticlesSectionListView = function ArticlesSectionListView(props: Props) {
  const [searchParams] = useSearchParams();
  const extendedSections = ['person', 'company', 'article'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const isPagination: boolean = useSelector(selectIsPagination);
  const {
    heading, articlesCollection, loading, articleCount, count, inGlobalSearch, query,
  } = props;
  const formattedCount = formatNumberWithCommas(count);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const host = window.location.href;
  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const convertDateFormat = (dateStr: string): string => {
    const [datePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-');
    const formattedDate = `${month}/${day}/${year}`;
    return formattedDate;
  };

  const createArticlesRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_articles_see_all_clicked', 0, {
        host,
      }),
    ));
    window.scrollTo(0, 0);
    return navigate(`${SEARCH_ROUTE}${query}&extend=article`, { state: { enableBackButton: true, previousPath } });
  };
  const { toggleVisible } = useArticleDetail({
    setVisible: undefined,
    visible: undefined,
  });

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${query}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  return (
    articlesCollection.length > 0 || loading ? (
      <StyleWrapper
        className={classNames({
          extendedSearch: isDetailedSearch,
        })}
      >
        {
          loading && !isDetailedSearch
            ? <ArticleListViewSkeleton />
            : (
              <>
                <HeadingWithShowAll
                  inGlobalSearch={inGlobalSearch}
                  isShowAllHidden={isDetailedSearch ? true
                    : articleCount && articleCount.length <= 2}
                  leftHeading={(
                    <SectionHeading customClassName="global-search-heading">{`${heading} (${formattedCount})`}</SectionHeading>)}
                  onClick={createArticlesRoute}
                  onSeelessClick={navigateToSearchResult}
                  isDetailedSearch={isDetailedSearch}
                />
                {
                  articlesCollection?.map((article) => (
                    <div
                      className="article-container"
                      onClick={() => toggleVisible(article)}
                      onKeyDown={() => toggleVisible(article)}
                      role="button"
                      tabIndex={0}
                    >
                      {article.image !== '' ? (
                        <ArticleImage color={article.alerts.length ? (article?.alerts[0]?.color) : ('#a3a2a2')}>
                          <LazyLoadImage
                            src={setImageSize(article.image, 54, 40, article.articleId)}
                            alt=""
                            effect="blur"
                          />
                        </ArticleImage>
                      ) : null}
                      <div className="article-info">
                        <Tooltip
                          trigger="hover"
                          placement="topLeft"
                          getPopupContainer={(e: HTMLElement) => e}
                          className="triggers-bullet-tooltip person-designation-tip"
                          color="#fff"
                          title={article.title}
                          arrow={false}
                        >
                          <span className="title">{article.title}</span>
                        </Tooltip>
                        <div className="details">
                          {article.companyName !== '' ? (
                            <Tooltip
                              trigger="hover"
                              placement="top"
                              getPopupContainer={(e: HTMLElement) => e}
                              className="triggers-bullet-tooltip person-designation-tip"
                              color="#fff"
                              title={article.companyName}
                              arrow={false}
                            >
                              <p className="company">{article.companyName}</p>
                            </Tooltip>
                          ) : null}
                          {article.domain !== '' ? (
                            <Tooltip
                              trigger="hover"
                              placement="top"
                              getPopupContainer={(e: HTMLElement) => e}
                              className="triggers-bullet-tooltip person-designation-tip"
                              color="#fff"
                              title={article.domain}
                              arrow={false}
                            >
                              <p className="domain">{article.domain}</p>
                            </Tooltip>
                          ) : null}
                          {article.date !== '' ? (
                            <Tooltip
                              trigger="hover"
                              placement="topLeft"
                              getPopupContainer={(e: HTMLElement) => e}
                              className="triggers-bullet-tooltip person-designation-tip"
                              color="#fff"
                              title={convertDateFormat(article.date)}
                              arrow={false}
                            >
                              <p className="date">{convertDateFormat(article.date)}</p>
                            </Tooltip>
                          ) : null}
                          {article.alerts.length ? (
                            article.alerts.map((item) => (
                              <Alert color={item.color}>
                                <p>{item.alert}</p>
                              </Alert>
                            ))
                          ) : null}
                        </div>

                      </div>

                    </div>
                  ))
                }
                {isDetailedSearch && isPagination && <ArticleListViewSkeleton />}
              </>
            )
        }
      </StyleWrapper>
    ) : (<> </>)
  );
};

ArticlesSectionListView.defaultProps = defaultProps;
export default ArticlesSectionListView;
